import { useState } from 'react';
import { Share2, Link, Receipt, CreditCard, Landmark, Trash2 } from 'lucide-react';

function DeleteConfirmModal({ fileName, onConfirm, onCancel, deleting }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '0 24px' }}
      onClick={onCancel}
    >
      <div
        style={{ background: '#fff', borderRadius: 16, padding: '24px 24px 20px', width: '100%', maxWidth: 340, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ width: 48, height: 48, borderRadius: 14, background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
          <Trash2 size={22} color="#DC2626" />
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#111', marginBottom: 6 }}>Delete report?</div>
        <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
          <span style={{ fontWeight: 700, color: '#374151' }}>"{fileName}"</span> will be permanently deleted.
        </div>
        <div style={{ fontSize: 12, color: '#DC2626', fontWeight: 600, marginBottom: 20 }}>
          This cannot be undone.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={deleting}
            style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#6B7280' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none', background: deleting ? '#FCA5A5' : '#DC2626', fontSize: 14, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', color: '#fff' }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FileRow({ file, onLoad, onDelete, onShareClick, onRestoreLocal, onClose }) {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(null);

  const isTransaction = file.reportType === 'credit_card' || file.reportType === 'bank';
  const count = (file.data || []).length;
  const subtitle = isTransaction
    ? (file.accountName ? `${file.accountName} · ` : '') + `${count} transaction${count !== 1 ? 's' : ''}`
    : `${count} expense${count !== 1 ? 's' : ''}`;

  const handleLoad = async () => {
    setLoading(true);
    try { await onLoad(file); onClose(); }
    catch (e) { console.error('Load failed:', e); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await onDelete(file); }
    catch (e) { console.error('Delete failed:', e); }
    finally { setDeleting(false); setConfirmDelete(false); }
  };

  const handleLocate = () => {
    setRestoreError(null);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = async (e) => {
      const pickedFile = e.target.files[0];
      if (!pickedFile) return;
      setRestoring(true);
      try {
        await onRestoreLocal(file.name, pickedFile);
      } catch {
        setRestoreError('Could not read that file. Please pick the correct JSON file.');
      } finally {
        setRestoring(false);
      }
    };
    input.click();
  };

  return (
    <div style={{
      background: file.localDataMissing ? '#FFFBEB' : '#F9FAFB',
      borderRadius: 12, padding: '14px 16px',
      border: `1px solid ${file.localDataMissing ? '#FCD34D' : '#E5E7EB'}`,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {file.name}
            </span>
            {file.isShared && (
              <span title="This report is shared with other users" style={{ display: 'flex', alignItems: 'center', flexShrink: 0, cursor: 'default' }}>
                <Link size={12} color="#0A7C8A" />
              </span>
            )}
            {file.local && !file.localDataMissing && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: '#DBEAFE', color: '#1D4ED8', flexShrink: 0 }}>LOCAL</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>{subtitle}</div>
        </div>

        {!isTransaction && onShareClick && (
          <button
            onClick={() => { onShareClick(file); onClose(); }}
            title="Share this report with another user or manage access"
            style={{ background: 'none', border: 'none', color: file.isShared ? '#0A7C8A' : '#D1D5DB', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
          >
            <Share2 size={16} />
          </button>
        )}

        {!file.localDataMissing && (
          <button
            onClick={handleLoad}
            disabled={loading || deleting}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)',
              color: '#fff', fontSize: 13, fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap', opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Loading…' : 'Load'}
          </button>
        )}

        <button
          onClick={() => setConfirmDelete(true)}
          disabled={loading || deleting}
          title="Delete"
          style={{ background: 'none', border: 'none', color: deleting ? '#9CA3AF' : '#EF4444', cursor: deleting ? 'not-allowed' : 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
        >
          <Trash2 size={16} />
        </button>
      </div>
      {confirmDelete && (
        <DeleteConfirmModal
          fileName={file.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
          deleting={deleting}
        />
      )}

      {file.localDataMissing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 12, color: '#92400E' }}>
            Local data not found on this device. Locate the JSON file to restore it.
          </div>
          {restoreError && <div style={{ fontSize: 12, color: '#DC2626' }}>{restoreError}</div>}
          <button
            onClick={handleLocate}
            disabled={restoring}
            style={{
              padding: '7px 14px', borderRadius: 8, border: '1.5px solid #D97706',
              background: '#FEF3C7', color: '#92400E', fontSize: 13, fontWeight: 700,
              cursor: restoring ? 'not-allowed' : 'pointer', alignSelf: 'flex-start',
              opacity: restoring ? 0.6 : 1,
            }}
          >
            {restoring ? 'Reading…' : 'Locate file…'}
          </button>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ icon, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#6B7280', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 12 }}>
      {icon}
      {label}
    </div>
  );
}

function LoadModal({ savedFiles, sharedWithMeFiles, onClose, onLoad, onLoadShared, onDelete, onShareClick, onRestoreLocal, user, showCc=false, showBanks=false }) {
  const [loadingShared, setLoadingShared] = useState(null);

  const handleSharedLoad = async (received) => {
    setLoadingShared(received.filename);
    try { await onLoadShared(received); onClose(); }
    catch (e) { console.error('Shared load failed:', e); }
    finally { setLoadingShared(null); }
  };

  const handleLocalFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target.result);
          if (Array.isArray(data)) { onLoad({ name: file.name.replace('.json', ''), data }); onClose(); }
          else alert('Invalid file format. Please select a valid expenses JSON file.');
        } catch { alert("Failed to parse file. Please ensure it's a valid JSON file."); }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const expenseFiles    = (savedFiles || []).filter(f => f.reportType === 'expense' || !f.reportType);
  const creditCardFiles = (savedFiles || []).filter(f => f.reportType === 'credit_card');
  const bankFiles       = (savedFiles || []).filter(f => f.reportType === 'bank');
  const received        = sharedWithMeFiles ?? [];

  const rowProps = { onLoad, onDelete, onShareClick, onRestoreLocal, onClose };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '20px', width: '100%', maxWidth: 390, maxHeight: '80vh', overflowY: 'auto', animation: 'slideUp 0.3s ease-out' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111' }}>Available Reports</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888', padding: 0, lineHeight: 1 }}>×</button>
        </div>

        {user && (
          <>
            {/* Expense Reports */}
            <div style={{ marginBottom: 24 }}>
              <SectionHeader icon={<Receipt size={12} />} label="Expense Reports" />
              {expenseFiles.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', background: '#F9FAFB', borderRadius: 12, color: '#9CA3AF', fontSize: 13 }}>
                  No expense reports yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {expenseFiles.map(f => <FileRow key={f.name} file={f} {...rowProps} />)}
                </div>
              )}
            </div>

            {/* Credit Card Files */}
            {showCc && creditCardFiles.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <SectionHeader icon={<CreditCard size={12} />} label="Credit Card Statements" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {creditCardFiles.map(f => <FileRow key={f.name} file={f} {...rowProps} />)}
                </div>
              </div>
            )}

            {/* Bank Files */}
            {showBanks && bankFiles.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <SectionHeader icon={<Landmark size={12} />} label="Bank Statements" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {bankFiles.map(f => <FileRow key={f.name} file={f} {...rowProps} />)}
                </div>
              </div>
            )}

            {/* Shared with Me */}
            {received.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <SectionHeader icon={<Share2 size={12} />} label="Shared with Me" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {received.map(r => {
                    const key = `${r.ownerUid}_${r.filename}`;
                    const isLoading = loadingShared === r.filename;
                    return (
                      <div key={key} style={{ background: '#F5F3FF', borderRadius: 12, padding: '14px 16px', border: '1px solid #DDD6FE', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.filename}
                          </div>
                          <div style={{ fontSize: 12, color: '#7C3AED' }}>Shared by {r.ownerEmail}</div>
                        </div>
                        <button
                          onClick={() => handleSharedLoad(r)}
                          disabled={isLoading}
                          style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: isLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: isLoading ? 0.6 : 1 }}
                        >
                          {isLoading ? 'Loading…' : 'Load'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Local file picker */}
        <div style={{ marginBottom: 20 }}>
          <SectionHeader icon={null} label="Load from File" />
          <button
            onClick={handleLocalFile}
            style={{ width: '100%', padding: '16px', borderRadius: 12, border: '1.5px dashed #0A7C8A', background: '#F0F4FF', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#0A7C8A', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            📁 Choose JSON File
          </button>
        </div>

        <div style={{ paddingTop: 4, borderTop: '1px solid #E5E7EB' }}>
          <button
            onClick={onClose}
            style={{ width: '100%', padding: '14px', borderRadius: 12, border: '1.5px solid #E5E7EB', background: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#6B7280', marginTop: 16 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoadModal;
